import { openKvStore } from './idb.js';
const DEFAULT_BUDGET = 500 * 1024 * 1024; // 500 MB
/**
 * Blob store for downloaded pages / files, with an LRU byte budget. Backed by
 * its own IndexedDB object store so it never collides with progress keys.
 *
 * Keys: `blob:<bookId>:<slot>` for blobs, `meta:<bookId>` for {@link BookCacheMeta}.
 */
export class MediaCache {
    #store;
    #budget;
    #now;
    constructor(opts = {}) {
        // Its own database — the default 'pore'/'kv' DB is created at v1 with only
        // the progress store, so a second store name there would never be created.
        this.#store = opts.store ?? openKvStore('pore-media', 'media');
        this.#budget = opts.budgetBytes ?? DEFAULT_BUDGET;
        this.#now = opts.now ?? Date.now;
    }
    async getBlob(bookId, slot) {
        const blob = await this.#store.get(blobKey(bookId, slot));
        if (blob)
            await this.#touch(bookId);
        return blob;
    }
    async putBlob(bookId, slot, blob, pageCount) {
        const meta = (await this.meta(bookId)) ?? { slots: [], bytes: 0, at: this.#now() };
        if (!meta.slots.includes(slot)) {
            meta.slots.push(slot);
            meta.bytes += blob.size;
        }
        meta.at = this.#now();
        if (pageCount !== undefined)
            meta.pageCount = pageCount;
        await this.#store.set(blobKey(bookId, slot), blob);
        await this.#store.set(metaKey(bookId), meta);
        await this.#evictToFit(bookId);
    }
    meta(bookId) {
        return this.#store.get(metaKey(bookId));
    }
    async removeBook(bookId) {
        const meta = await this.meta(bookId);
        if (!meta)
            return;
        for (const slot of meta.slots)
            await this.#store.delete(blobKey(bookId, slot));
        await this.#store.delete(metaKey(bookId));
    }
    async totalBytes() {
        let sum = 0;
        for (const b of await this.#books())
            sum += b.meta.bytes;
        return sum;
    }
    get budgetBytes() {
        return this.#budget;
    }
    // ---- internals ----------------------------------------------------------
    async #touch(bookId) {
        const meta = await this.meta(bookId);
        if (meta) {
            meta.at = this.#now();
            await this.#store.set(metaKey(bookId), meta);
        }
    }
    async #books() {
        const keys = await this.#store.keys();
        const out = [];
        for (const k of keys) {
            if (!k.startsWith('meta:'))
                continue;
            const bookId = k.slice('meta:'.length);
            const meta = await this.#store.get(k);
            if (meta)
                out.push({ bookId, meta });
        }
        return out;
    }
    /** Drop whole books, least-recently-used first, until under budget. Never the one in use. */
    async #evictToFit(keepBookId) {
        let books = await this.#books();
        let total = books.reduce((s, b) => s + b.meta.bytes, 0);
        if (total <= this.#budget)
            return;
        books = books
            .filter((b) => b.bookId !== keepBookId)
            .sort((a, b) => a.meta.at - b.meta.at);
        for (const victim of books) {
            if (total <= this.#budget)
                break;
            await this.removeBook(victim.bookId);
            total -= victim.meta.bytes;
        }
    }
}
const blobKey = (bookId, slot) => `blob:${bookId}:${slot}`;
const metaKey = (bookId) => `meta:${bookId}`;
//# sourceMappingURL=media-cache.js.map